/**
 * App Page Settings - HYB MASTER SHOP
 * provides per-page configuration forms with modular structure.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Loader2,
  ShieldOff,
  Wrench,
  LayoutDashboard,
} from 'lucide-react';

import {
  fetchAppSettings,
  updateAppSettings,
} from '../api/admin';

import { useAuth } from '../contexts/AuthContext';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';
import { cn } from '../utils/responsive';
import { logger } from '../utils/logger';
import { MaintenanceTab } from '../components/MaintenanceTab';

// App Settings Modular Assets
import {
  type PageKey,
  type AppPageSettingsFormValues,
  PAGE_DEFINITIONS,
  cloneDefaultValues,
  mapIncomingSettings,
  buildOutgoingSettings,
  BannersTab,
  FirstPageTab,
  TermPageTab,
  HomePageTab,
  CheckoutPageTab,
  HistoryPageTab,
  MyPageTab,
  MyLevelPageTab,
  WalletTermPageTab,
} from './settings';

export function AppSettings() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const isMobileOrTablet = useIsMobileOrTablet();

  // Navigation State
  const [activeTab, setActiveTab] = useState<PageKey | 'banners' | 'maintenance'>(PAGE_DEFINITIONS[0].id);
  const [savingPage, setSavingPage] = useState<PageKey | 'banners' | null>(null);

  const {
    reset,
    setValue,
    watch,
  } = useForm<AppPageSettingsFormValues>({
    defaultValues: cloneDefaultValues(),
  });

  // Data Fetching
  const { isError, isLoading: loadingSettings } = useQuery(['appSettings'], fetchAppSettings, {
    onSuccess: (data: any) => {
      // fetchAppSettings returns { settings: { pages: {...}, ... } }
      // mapIncomingSettings expects { pages: {...}, ... }
      const mapped = mapIncomingSettings(data?.settings || data);
      reset(mapped);
    },
    onError: () => {
      toast.error('ไม่สามารถโหลดข้อมูลการตั้งค่าได้');
    },
    staleTime: 5 * 60 * 1000,
  });


  // Data Updating
  const updateSettingsMutation = useMutation(updateAppSettings, {
    onSuccess: async () => {
      // Invalidate and refetch to get updated data
      await queryClient.invalidateQueries(['appSettings']);
      const newData = await queryClient.fetchQuery(['appSettings'], fetchAppSettings) as any;
      // Reset form with new values (extract settings from wrapper)
      const mapped = mapIncomingSettings(newData?.settings || newData);
      reset(mapped);
      toast.success('บันทึกการตั้งค่าเรียบร้อยแล้ว');
    },
    onError: (error: any) => {
      logger.error('Save error:', error);
      toast.error(error?.message || 'เกิดข้อผิดพลาดในการบันทึก');
    },
    onSettled: () => {
      setSavingPage(null);
    },
  });


  // Logic Constants
  const loadFailed = isError;

  /**
   * Execute API call to save page settings
   */
  const handleSavePage = async (
    pageId: PageKey,
    pageValues: AppPageSettingsFormValues[PageKey],
    additionalUpdates: Array<{ pageId: PageKey; values: AppPageSettingsFormValues[PageKey] }> = [],
  ) => {
    try {
      setSavingPage(pageId);
      const updates = [{ pageId, values: pageValues }, ...additionalUpdates];

      // Get cached response and extract the inner settings object
      // Query cache stores { settings: { pages: {...}, ... } } from fetchAppSettings
      const cachedData = queryClient.getQueryData<any>(['appSettings']) || {};
      let baseSettings = cachedData.settings || cachedData || {};

      updates.forEach(update => {
        baseSettings = buildOutgoingSettings(baseSettings as any, update.pageId, update.values);
      });

      await updateSettingsMutation.mutateAsync(baseSettings);
    } catch (e) {
      logger.error('Handle save error:', e);
    }
  };


  // Navigation Categories
  const navSections: Array<{
    title: string;
    items: Array<{ id: PageKey | 'banners' | 'maintenance'; icon: any; label: string }>;
  }> = [
      {
        title: 'Authentication',
        items: [
          { id: 'firstPage', icon: PAGE_DEFINITIONS[0].icon, label: 'First & Login' },
        ],
      },
      {
        title: 'Shopping',
        items: [
          { id: 'homePage', icon: PAGE_DEFINITIONS[3].icon, label: 'Home Page' },
          { id: 'checkoutPage', icon: PAGE_DEFINITIONS[5].icon, label: 'Checkout' },
        ],
      },
      {
        title: 'User Pages',
        items: [
          { id: 'myPage', icon: PAGE_DEFINITIONS[7].icon, label: 'MY Page' },
          { id: 'myLevelPage', icon: PAGE_DEFINITIONS[8].icon, label: 'MY Level' },
          { id: 'historyPage', icon: PAGE_DEFINITIONS[6].icon, label: 'Order History' },
        ],
      },
      {
        title: 'Content',
        items: [
          { id: 'banners', icon: LayoutDashboard, label: 'News & Banner' },
          { id: 'termPage', icon: PAGE_DEFINITIONS[2].icon, label: 'Terms Page' },
        ],
      },
      {
        title: 'System',
        items: [
          { id: 'walletTermPage', icon: PAGE_DEFINITIONS[9].icon, label: 'Wallet Settings' },
          { id: 'maintenance', icon: Wrench, label: 'Maintenance' },
        ],
      },
    ];

  // Render specialized tab based on active section
  const renderTabContent = () => {
    // Special case: Banners
    if (activeTab === 'banners') {
      return (
        <BannersTab
          isMobileOrTablet={isMobileOrTablet}
          savingPage={savingPage}
          onSave={async () => {
            queryClient.invalidateQueries(['banners']);
          }}
        />
      );
    }

    if (activeTab === 'maintenance') {
      return <MaintenanceTab />;
    }

    const definition = PAGE_DEFINITIONS.find(d => d.id === activeTab);
    if (!definition) return null;

    const commonProps = {
      definition: definition!, // We checked !definition above
      watch,
      setValue: setValue as any,
      isMobileOrTablet,
      saving: updateSettingsMutation.isLoading,
      savingPage,
      onSave: handleSavePage,
      loadFailed,
    };

    switch (activeTab) {
      case 'firstPage':
      case 'loginPage':
        return <FirstPageTab {...commonProps} />;
      case 'termPage':
        return <TermPageTab {...commonProps} />;
      case 'homePage':
        return <HomePageTab {...commonProps} />;
      case 'checkoutPage':
        return <CheckoutPageTab {...commonProps} />;
      case 'historyPage':
        return <HistoryPageTab {...commonProps} />;
      case 'myPage':
        return <MyPageTab {...commonProps} />;
      case 'myLevelPage':
        return <MyLevelPageTab {...commonProps} />;
      case 'walletTermPage':
        return <WalletTermPageTab {...commonProps} />;
      default:
        return null;
    }
  };

  // Permission Check
  if (!hasPermission('write')) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-4">
          <ShieldOff className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 italic">Access Denied</h2>
          <p className="text-gray-500 max-w-sm">
            ขออภัย คุณไม่มีสิทธิ์ในการแก้ไขการตั้งค่าแอป โปรดติดต่อผู้ดูแลระบบหากต้องการสิทธิ์เข้าถึง
          </p>
        </div>
      </div>
    );
  }

  if (loadingSettings) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-yellow-500" />
          <p className="text-sm font-medium text-stone-500 animate-pulse">กำลังโหลดข้อมูลแอป...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-stone-950 lg:flex-row">
      {/* Sidebar Navigation */}
      {!isMobileOrTablet && (
        <aside className="w-full border-r border-stone-200 dark:border-white/5 bg-stone-50/50 dark:bg-stone-900/20 lg:w-72">
          <div className="sticky top-0 h-screen overflow-y-auto px-4 py-8 custom-scrollbar">
            <h1 className="mb-8 px-4 text-xl font-black tracking-tight text-stone-900 dark:text-white flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-yellow-400 flex items-center justify-center text-white shadow-soft">H</span>
              APP SETTINGS
            </h1>

            <nav className="space-y-6">
              {navSections.map((section) => (
                <div key={section.title}>
                  <h3 className="mb-3 px-4 text-[10px] font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                    {section.title}
                  </h3>
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = activeTab === item.id;
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => setActiveTab(item.id)}
                          className={cn(
                            "group flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200",
                            isActive
                              ? "bg-yellow-400 text-white shadow-soft"
                              : "text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-stone-900 dark:hover:text-white"
                          )}
                        >
                          <Icon className={cn(
                            "h-4 w-4 transition-transform group-hover:scale-110",
                            isActive ? "text-white" : "text-stone-400 dark:text-stone-500"
                          )} />
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </aside>
      )}

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden bg-stone-50/50 dark:bg-transparent">
        {/* Mobile Tab Header */}
        {isMobileOrTablet && (
          <div className="sticky top-0 z-20 border-b border-stone-200 dark:border-white/5 bg-white/80 dark:bg-stone-950/80 p-4 backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-yellow-400 flex items-center justify-center text-white font-bold">H</div>
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as any)}
                className="flex-1 bg-transparent text-sm font-bold text-stone-900 dark:text-white focus:outline-none"
              >
                {navSections.map(section => (
                  <optgroup key={section.title} label={section.title}>
                    {section.items.map(item => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className={cn("p-4 lg:p-8 max-w-5xl mx-auto", isMobileOrTablet && "pb-24")}>
          {loadFailed && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400">
              ⚠️ ไม่สามารถดึงข้อมูลการตั้งค่าปัจจุบันได้ ระบบแสดงค่าเริ่มต้น กรุณาลองบันทึกอีกครั้ง
            </div>
          )}

          {renderTabContent()}
        </div>
      </main>
    </div>
  );
}

export default AppSettings;

